import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { User, LoginRequest, LoginResponse, SecurityRoleCode, Permissions } from '../models/security.model';

/**
 * Authentication service
 * Handles user login, logout, and permission checking
 * Replaces ExtJS SecurityRole and otbSecurity plugin functionality
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private api: ApiService) {
    // Load user from localStorage on init
    this.loadUserFromStorage();
  }

  /**
   * Login with username and password
   */
  login(credentials: LoginRequest): Observable<User> {
    return this.api.post<LoginResponse>('/auth/login', credentials).pipe(
      tap(response => {
        this.setCurrentUser(response.user);
        if (response.token) {
          localStorage.setItem('auth_token', response.token);
        }
      }),
      map(response => response.user),
      catchError(error => {
        console.error('Login failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout current user
   */
  logout(): void {
    this.api.post('/auth/logout', {}).subscribe({
      complete: () => {
        this.clearCurrentUser();
      },
      error: () => {
        // Clear user even if logout API fails
        this.clearCurrentUser();
      }
    });
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  /**
   * Check if user has a specific role
   */
  hasRole(roleCode: SecurityRoleCode): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    return user.roles.some(role => role.code === roleCode);
  }

  /**
   * Check if user is admin
   * Used extensively in ExtJS for setup view access
   */
  isAdmin(): boolean {
    return this.hasRole(SecurityRoleCode.ADMIN);
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Admin has all permissions
    if (this.isAdmin()) return true;

    // Check if any of user's roles have the permission
    return user.roles.some(role =>
      role.permissions.includes(permission)
    );
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * Set current user
   */
  private setCurrentUser(user: User): void {
    localStorage.setItem('current_user', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  /**
   * Clear current user
   */
  private clearCurrentUser(): void {
    localStorage.removeItem('current_user');
    localStorage.removeItem('auth_token');
    this.currentUserSubject.next(null);
  }

  /**
   * Load user from localStorage
   */
  private loadUserFromStorage(): void {
    const userJson = localStorage.getItem('current_user');
    if (userJson) {
      try {
        const user = JSON.parse(userJson);
        this.currentUserSubject.next(user);
      } catch (e) {
        console.error('Failed to parse stored user:', e);
        this.clearCurrentUser();
      }
    }
  }

  /**
   * Get auth token for HTTP requests
   */
  getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }
}
